<template>
  <div
    v-if="context"
    :class="containerClasses"
    data-walkthrough-id="workflow-review-communication"
    data-behaviour="workflow-review-sidepane"
    data-testid="sidepane-container"
    :data-context-id="context.id"
    :data-context-type="context.itemClass"
    @click.stop="clearHighlightedCard"
  >
    <SfHeightWatcher
      :variable-name="'--communication-sidepane-controls-height'"
    >
      <div :class="topComponentsPadding">
        <div class="sf-level sf-level-between sf-below-3">
          <workflow-review-sidepane-tags-filter :context="context" />
          <div class="tw-ml-auto">
            <SfButtonIcon icon="arrow-right" @click="close" />
          </div>
        </div>
        <div class="sf-level sf-level-between">
          <div
            class="
              sf-font-l sf-c-text-dark sf-fwdb sf-break-all sf-below-2
              tw-break-normal
            "
            data-testid="sf-sidepane-context-name"
          >
            {{ context.itemName }}
          </div>
        </div>
        <div class="sf-below-2">
          <new-remark-button
            :owner-type="context.itemClass"
            :owner-id="context.id"
            :ledger-id="ledgerId"
            :ledger-label="ledgerLabel"
            extra-classes="sf-full sf-alignc"
          />
        </div>
      </div>
    </SfHeightWatcher>
    <sidepane-communication
      empty-translation-key="communication.workflow_review_empty_state"
      :is-body-scrollable="false"
      :show-empty-state-image="false"
      :show-timeline="false"
    />
  </div>
</template>

<script>
import { mapActions as mapPiniaActions, mapState as mapPiniaState } from 'pinia'
import { mapActions, mapState } from 'vuex'

import WorkflowReviewSidepaneTagsFilter from './workflow-review-sidepane-tags-filter'
import { isFeatureSupported } from '@app/adapters/silverfin'
import SidepaneCommunication from '@app/application/communication/sections/communication/components/sidepane-communication'
import NewRemarkButton from '@app/shared/vue/components/new-remark/new-remark-button'
import { SfButtonIcon } from '@app/shared/vue/components/sf-button'
import SfHeightWatcher from '@app/shared/vue/components/sf-height-watcher.vue'
import registerSidepanePageVisitMixin from '@app/shared/vue/mixins/register-sidepane-page-visit'

import { useWorfklowReviewItems } from '../store/pinia/workflow-review-items'
import { useDrawer } from '@app/shared/pinia/drawer'

export default {
  name: 'WorkflowReviewSidepane',
  components: {
    NewRemarkButton,
    SfButtonIcon,
    SidepaneCommunication,
    SfHeightWatcher,
    WorkflowReviewSidepaneTagsFilter,
  },
  mixins: [registerSidepanePageVisitMixin],
  props: {
    ledgerId: {
      type: [String, Number],
      required: true,
    },
    ledgerLabel: {
      type: String,
      required: true,
    },
  },
  emits: ['clearAnchor'],
  setup() {
    const supportsCommunicationsRework = isFeatureSupported(
      'supports_communications_rework'
    )

    return {
      supportsCommunicationsRework,
      containerClasses: supportsCommunicationsRework
        ? ['tw-flex', 'tw-flex-col', 'tw-h-full']
        : ['tw-px-6', 'tw-py-3', 'sf-pad-b-4'],
      topComponentsPadding: supportsCommunicationsRework
        ? ['tw-px-6', 'tw-pt-3']
        : [],
    }
  },
  computed: {
    ...mapState({
      cardsFilter: (state) => state.cardsFilter,
      highlightedCard: (state) => state.ui.highlightedCard,
      fetchingCommunicationsStatus: (state) =>
        state.ui.fetchingCommunicationsStatus,
    }),
    ...mapPiniaState(useWorfklowReviewItems, {
      context: 'currentReviewItem',
    }),
    ...mapPiniaState(useDrawer, ['isRightDrawerOpen']),
    // needed for the registerSidepanePageVisit mixin
    contextType() {
      return this.context?.itemClass
    },
    contextId() {
      return this.context?.id
    },
  },
  created() {
    window.eventHub.$on('openSidepane', this.effectiveOpenDrawer)
  },
  beforeUnmount() {
    window.eventHub.$off('openSidepane', this.effectiveOpenDrawer)
  },
  methods: {
    ...mapActions(['unsetHighlightedCard']),
    ...mapPiniaActions(useDrawer, ['openRightDrawer', 'closeDrawer']),
    clearHighlightedCard() {
      if (this.highlightedCard.id) {
        this.unsetHighlightedCard()
        window.eventHub.$emit('clearAnchor')
      }
    },
    close() {
      this.closeDrawer()
    },
    effectiveOpenDrawer() {
      this.openRightDrawer('communication')
    },
  },
}
</script>
